import { Component, OnDestroy, OnInit, inject, signal } from '@angular/core';
import { ActivatedRoute, Router, RouterOutlet } from '@angular/router';
import { filter, Subject, takeUntil } from 'rxjs';
import { AuthService } from './core/services/auth.service';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { SheetService } from './shared/services/sheet.service';
import { PropertyView } from './features/property/components/property-view/property-view';
import { PropertyService } from './features/property/services/property.service';
import { Property } from './features/property/models/property.model';
import { LAYOUT_IMPORTS } from './shared/layout/layout.imports';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [RouterOutlet, LAYOUT_IMPORTS],
  templateUrl: './app.html',
  styleUrl: './app.scss'
})
export class App implements OnInit, OnDestroy {
  protected readonly title = signal('horizinns');
  private readonly authService = inject(AuthService);
  private readonly bottomSheet = inject(MatBottomSheet);
  private readonly sheetService = inject(SheetService);
  private readonly propertyService = inject(PropertyService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly destroy$ = new Subject<void>();
  readonly currentProperty = signal<Property | null>(null);

  ngOnInit(): void {
    this.route.queryParams.pipe(
      filter(q => !!q['property']),
      takeUntil(this.destroy$)
    ).subscribe(params => {
      const propertyId = params['property'] as string;
      this.bottomSheet.dismiss();

      this.propertyService.getPropertyById(propertyId).subscribe({
        next: (property) => {
          this.currentProperty.set(property);
          this.openPropertySheet(property);
        },
        error: (err) => {
          console.error('Failed to load property', propertyId, err);
          this.clearPropertyQueryParam();
        }
      });
    });
  }

  private openPropertySheet(property: Property): void {
    const ref = this.sheetService.open(PropertyView, {
      inputProperty: property
    }).then(result => {
      console.log('Sheet closed with:', result);
      this.clearPropertyQueryParam();
    }).catch(err => {
      console.error('Sheet error', err);
      this.clearPropertyQueryParam();
    });
  }

  private clearPropertyQueryParam(): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { property: null },
      queryParamsHandling: 'merge',
      replaceUrl: true
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}